import { useState } from "react";
import { Accessibility, X } from "lucide-react";
import { useA11y } from "@/context/A11yContext";
import { cn } from "@/lib/utils";

export default function A11ySettings() {
    const { prefs, setPref } = useA11y();
    const [open, setOpen] = useState(false);

    const options = [
        {
            key: "dyslexia",
            label: "Dyslexia-friendly font",
            desc: "Switches to a font optimized for reading with dyslexia (Atkinson Hyperlegible).",
        },
        {
            key: "highContrast",
            label: "High contrast",
            desc: "Maximum contrast black-on-white theme, ideal for low vision.",
        },
        {
            key: "largerText",
            label: "Larger text",
            desc: "Bumps base font size by ~12% for easier reading.",
        },
    ];

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                aria-label="Open accessibility settings"
                data-testid="a11y-settings-toggle"
                className="inline-flex items-center gap-2 h-11 px-4 rounded-md border border-border bg-card text-primary hover:border-primary/40 transition-colors text-sm font-medium"
            >
                <Accessibility className="w-4 h-4" strokeWidth={1.5} />
                Accessibility
            </button>

            {open && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="a11y-title"
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    data-testid="a11y-settings-dialog"
                >
                    <button
                        aria-label="Close dialog"
                        onClick={() => setOpen(false)}
                        className="absolute inset-0 bg-primary/40 backdrop-blur-sm"
                    />
                    <div className="relative bg-card rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-auto border border-border">
                        <div className="p-6 border-b border-border flex items-start justify-between gap-3">
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.2em] text-accent">
                                    Preferences
                                </div>
                                <h2 id="a11y-title" className="font-serif text-2xl text-primary mt-1">
                                    Accessibility settings
                                </h2>
                                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                                    Adjust typography, contrast, and motion. Your choices save in your
                                    browser — we never see them.
                                </p>
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                aria-label="Close"
                                data-testid="a11y-close"
                                className="h-11 w-11 grid place-items-center rounded-md hover:bg-muted transition-colors"
                            >
                                <X className="w-5 h-5" strokeWidth={1.5} />
                            </button>
                        </div>
                        <div className="p-6 space-y-2">
                            {options.map((o) => (
                                <label
                                    key={o.key}
                                    className={cn(
                                        "flex items-start gap-3 p-3 rounded-md border border-transparent",
                                        "hover:bg-muted/50 hover:border-border cursor-pointer transition-colors",
                                    )}
                                >
                                    <input
                                        type="checkbox"
                                        checked={!!prefs[o.key]}
                                        onChange={(e) => setPref(o.key, e.target.checked)}
                                        className="mt-1.5 accent-accent-bright w-4 h-4"
                                        data-testid={`a11y-toggle-${o.key}`}
                                    />
                                    <div>
                                        <div className="text-sm font-medium text-primary">{o.label}</div>
                                        <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                                            {o.desc}
                                        </div>
                                    </div>
                                </label>
                            ))}
                            <div className="mt-4 p-3 rounded-md bg-muted/50 text-xs text-muted-foreground leading-relaxed">
                                <strong className="text-primary font-medium">Keyboard shortcuts:</strong> Press{" "}
                                <kbd className="px-1.5 py-0.5 rounded bg-background border border-border font-mono text-[11px]">?</kbd>{" "}
                                anywhere to see the full list.
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
